/**
 * Campaign Setup — thin wrappers around `/admin/launch/*`.
 *
 * A launch plan is a DRAFT. Creating or editing one changes nothing in the
 * tenant: no accounts, no candidate, no scopes. Only `commitLaunchPlan`
 * creates anything, and it does so in a single server-side transaction.
 *
 * Naming: the UI says "Campaign Setup" because that is what the person using
 * it is doing. The code says `launch` because "Campaign" already means an SMS
 * send in this system (`VoterCampaign`) — see backend `models/candidate.py`.
 *
 * Pattern mirrors `src/api/adminUsers.ts`: bare `client.<verb>`, typed returns,
 * no mock fixtures.
 */
import client from '@/api/client';
import type { UserScopeInput } from '@/types';

// ---------------------------------------------------------------------------
// Draft shape — keep in sync with backend `schemas/launch.py`.
// ---------------------------------------------------------------------------

export interface LaunchRace {
  /** Lowercase-kebab, permanent. Never reuse one — it re-attributes history. */
  slug: string;
  full_name: string;
  office: string;
  /** Display only. The authoritative turf is `reach`. */
  district_label?: string | null;
  party?: string | null;
  election_date?: string | null;
}

export interface LaunchRosterMember {
  email: string;
  full_name?: string | null;
  role: string;
  /** 'candidate' receives the full reach; 'staff' gets exactly `scopes`. */
  relation: 'candidate' | 'staff';
  scopes: UserScopeInput[];
  can_provision: boolean;
}

export interface LaunchCompliance {
  office_type?: string | null;
  /** null = UNLIMITED. Not unknown, not zero. */
  limit_cents?: number | null;
  jurisdiction?: string | null;
}

export interface LaunchDraft {
  race: LaunchRace;
  reach: UserScopeInput[];
  roster: LaunchRosterMember[];
  compliance: LaunchCompliance;
  /** Empty reach means county-wide, i.e. the whole roll. Must be explicit. */
  county_wide_confirmed: boolean;
}

export interface LaunchPlan {
  id: number;
  name: string;
  status: 'draft' | 'committed' | 'abandoned';
  draft: LaunchDraft | null;
  committed_at?: string | null;
  committed_candidate_id?: number | null;
  created_at: string;
  updated_at?: string | null;
}

// ---------------------------------------------------------------------------
// Preflight
// ---------------------------------------------------------------------------

export interface PreflightFinding {
  severity: 'block' | 'warn';
  /** Stable key (e.g. 'email_taken') — attach to a field, don't match prose. */
  code: string;
  message: string;
  subject?: string | null;
}

export interface ReachResolution {
  precinct_count: number;
  voter_count: number;
  /** True when the reach is empty — which means EVERYTHING, not nothing. */
  tenant_global: boolean;
}

export interface SeatDemand {
  seat_class: string;
  needed: number;
  /** null when unlimited — check `unlimited`, don't compare this. */
  remaining: number | null;
  unlimited: boolean;
}

export interface PreflightReport {
  ready: boolean;
  findings: PreflightFinding[];
  reach: ReachResolution;
  seats: SeatDemand[];
  account_count: number;
  scope_row_count: number;
  invite_count: number;
}

export interface LaunchInvite {
  email: string;
  full_name?: string | null;
  role: string;
  /** Shown ONCE. Not stored anywhere and not recoverable — copy it now. */
  token: string;
}

export interface LaunchCommitResult {
  plan_id: number;
  candidate_id: number;
  created_user_ids: number[];
  invites: LaunchInvite[];
  /** True when this matched an earlier commit and nothing new was created. */
  idempotent_replay: boolean;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function listLaunchPlans(): Promise<LaunchPlan[]> {
  const res = await client.get<LaunchPlan[]>('/admin/launch');
  return res.data;
}

export async function createLaunchPlan(draft: LaunchDraft, name?: string) {
  const res = await client.post<LaunchPlan>('/admin/launch', { draft, name });
  return res.data;
}

export async function getLaunchPlan(id: number): Promise<LaunchPlan> {
  const res = await client.get<LaunchPlan>(`/admin/launch/${id}`);
  return res.data;
}

/** Whole-document replace. The wizard holds the entire draft anyway. */
export async function updateLaunchPlan(id: number, draft: LaunchDraft, name?: string) {
  const res = await client.patch<LaunchPlan>(`/admin/launch/${id}`, { draft, name });
  return res.data;
}

/** Check readiness. Changes nothing — safe to call on every step change. */
export async function preflightLaunchPlan(id: number): Promise<PreflightReport> {
  const res = await client.post<PreflightReport>(`/admin/launch/${id}/preflight`);
  return res.data;
}

/**
 * Start the campaign. One transaction — everything or nothing.
 *
 * `idempotency_key` must be STABLE for a given attempt: if the request times
 * out and the user retries, the same key is what stops a second roster being
 * created. Generate it once when the plan is opened, not per click.
 */
export async function commitLaunchPlan(
  id: number,
  idempotencyKey: string,
  inviteTtlDays = 14,
): Promise<LaunchCommitResult> {
  const res = await client.post<LaunchCommitResult>(`/admin/launch/${id}/commit`, {
    idempotency_key: idempotencyKey,
    invite_ttl_days: inviteTtlDays,
  });
  return res.data;
}

export async function abandonLaunchPlan(id: number): Promise<LaunchPlan> {
  const res = await client.post<LaunchPlan>(`/admin/launch/${id}/abandon`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Role options
// ---------------------------------------------------------------------------

export interface RoleOption {
  value: string;
  rank: number;
  /** False = above the caller's ceiling. Render disabled, don't hide it. */
  grantable: boolean;
}

export async function fetchRoleOptions(): Promise<RoleOption[]> {
  const res = await client.get<{ roles: RoleOption[] }>('/admin/users/role-options');
  return res.data.roles;
}

export interface SeatUsage {
  used: number;
  limit: number | null;
  unlimited: boolean;
  remaining: number | null;
}

export interface SeatSnapshot {
  usage: Record<string, SeatUsage>;
  /**
   * Role wire value -> seat class, served by the API on purpose: the grouping
   * lives in `services/seats.py` and duplicating it in the frontend is how the
   * two drift the next time a role is added.
   */
  role_classes: Record<string, string>;
}

/**
 * Seat usage for the caller's tenant. Readable by anyone who may provision —
 * the roster step needs "4 of 25 field seats left" while someone is still
 * typing, not after they have entered thirty people.
 */
export async function fetchSeats(): Promise<SeatSnapshot> {
  const res = await client.get<SeatSnapshot>('/admin/seats');
  return { usage: res.data.usage ?? {}, role_classes: res.data.role_classes ?? {} };
}

// ---------------------------------------------------------------------------
// Starter routes
// ---------------------------------------------------------------------------

export interface StarterRoute {
  id: number;
  name: string;
  precinct_num: string | null;
  door_count: number;
  est_hours: number | null;
  status: string;
}

/**
 * Draft the campaign's first walking routes from its own district.
 *
 * A separate call from the commit ON PURPOSE: route building can reach an
 * OSRM server over the network, and a routing hiccup must never roll back a
 * campaign's accounts. Safe to retry — the server records that the step ran
 * on the plan, so a second call is a no-op rather than three more routes.
 */
export async function buildStarterRoutes(id: number): Promise<{
  created: number;
  routes: StarterRoute[];
}> {
  const res = await client.post(`/admin/launch/${id}/starter-routes`);
  return res.data;
}
